import { useCallback, useEffect, useState } from 'react';
import { ApiError, getSpaces, type SpaceSummary } from '@/lib/api';

/**
 * The caller's spaces (FIL-1).
 *
 * Deliberately a second hook rather than a field on `useMe`: the two have
 * different lifetimes. The session is fetched once and rarely changes; the space
 * list changes whenever a handshake completes or a space is used, and will want
 * refetching on its own.
 *
 * **This is not authorization.** SEC-5 puts that in the API. What comes back
 * here decides what the UI shows; it never decides what the user may do.
 */

export type SpacesState =
  | { kind: 'loading' }
  /**
   * An empty list is a loaded state, not an error — PERM-2 gives a member with
   * no grants an empty Files surface, and that is the system working.
   */
  | { kind: 'loaded'; spaces: SpaceSummary[] }
  /** `status` carried for the same reason as in `useMe`. `0` is network-level. */
  | { kind: 'error'; status: number };

export interface UseSpacesResult {
  state: SpacesState;
  /** After a failure. Clears the list back to `loading` first. */
  retry: () => void;
  /**
   * After a mutation — connecting, accepting, severing.
   *
   * Deliberately does **not** reset to `loading`. The rows on screen are still
   * true while the refetch is in flight, and blanking a table the user is
   * looking at to re-render the same rows a moment later reads as a glitch.
   */
  refresh: () => void;
}

export function useSpaces(): UseSpacesResult {
  const [state, setState] = useState<SpacesState>({ kind: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    getSpaces(controller.signal)
      .then(({ spaces }) => {
        setState({ kind: 'loaded', spaces });
      })
      .catch((error: unknown) => {
        // An abort is our own doing, not a failure.
        if (error instanceof Error && error.name === 'AbortError') return;
        setState({ kind: 'error', status: error instanceof ApiError ? error.status : 0 });
      });

    return () => {
      controller.abort();
    };
  }, [reloadToken]);

  const refresh = useCallback(() => {
    setReloadToken((current) => current + 1);
  }, []);

  // The loading reset belongs in the handler, not the effect: calling setState
  // in an effect body triggers a cascading render.
  const retry = useCallback(() => {
    setState({ kind: 'loading' });
    setReloadToken((current) => current + 1);
  }, []);

  return { state, retry, refresh };
}
